import modulesRaw from '../../modules.txt?raw';
import { SEB_TEST_DATA } from './sebTestData';

const TYPE_CODE_RE = /^[HML]{3}$/;
const HEADINGS = [
    '유형 정의',
    '다른 사람에게 보이는 모습',
    '불러일으킬 수 있는 오해',
    '주의할 점',
    '편안함을 느끼는 상황',
    '중요하게 여기는 것'
];

const SEB_NAMES = {
    HHH: "완성형 멘토",
    HHL: "평화로운 수호자",
    HLH: "철저한 전략가",
    HLL: "고요한 관찰자",
    LHH: "열정적인 구도자",
    LHL: "민감한 중재자",
    LLH: "불굴의 개척자",
    LLL: "창의적 유목민"
};

const normalizeText = (text) =>
    String(text || '')
        .replace(/\r/g, '\n')
        .split('\n')
        .map((line) => line.trim())
        .filter((line, idx, arr) => !(line === '' && arr[idx - 1] === ''))
        .join('\n')
        .replace(/관계를 억지로 유지하기보다 관계를 억지로 유지하기보다/g, '관계를 억지로 유지하기보다')
        .trim();

const sectionText = (fullText, startHeading, endHeading) => {
    const start = fullText.indexOf(startHeading);
    if (start === -1) return '';
    const from = start + startHeading.length;
    const end = endHeading ? fullText.indexOf(endHeading, from) : -1;
    const raw = end === -1 ? fullText.slice(from) : fullText.slice(from, end);
    return normalizeText(raw);
};

const parseModules = (raw) => {
    const lines = String(raw || '')
        .split(/\r?\n/)
        .map((line) => line.trim());

    const typeIndices = [];
    lines.forEach((line, idx) => {
        if (TYPE_CODE_RE.test(line)) typeIndices.push({ code: line, idx });
    });

    const parsed = {};
    typeIndices.forEach((cur, i) => {
        const nextIdx = i < typeIndices.length - 1 ? typeIndices[i + 1].idx : lines.length;
        const block = lines.slice(cur.idx + 1, nextIdx).join('\n');

        const desc = sectionText(block, HEADINGS[0], HEADINGS[1]);
        const social = sectionText(block, HEADINGS[1], HEADINGS[2]);
        const blind = sectionText(block, HEADINGS[2], HEADINGS[3]);
        const caution = sectionText(block, HEADINGS[3], HEADINGS[4]);
        const comfort = sectionText(block, HEADINGS[4], HEADINGS[5]);
        const value = sectionText(block, HEADINGS[5], null);

        parsed[cur.code] = {
            type_code: cur.code,
            name: SEB_NAMES[cur.code] || `${cur.code} 유형`,
            definition: desc,
            social_view: social,
            misunderstanding: blind,
            caution,
            comfort_zone: comfort,
            core_value: value,
            summary: desc,
            description: desc,
            detailed_analysis: `${social} ${blind}`.trim(),
            social_role: social,
            blind_spots: blind,
            action_plan: caution,
            academic_report: {
                core_driver: value,
                psychological_basis: comfort,
                multidimensional_analysis: `${social} ${blind}`.trim(),
                growth_trajectory: caution
            }
        };
    });

    return parsed;
};

const baseByType = Object.fromEntries(
    (SEB_TEST_DATA.results || []).map((item) => [item.type_code, item])
);

const moduleByType = parseModules(modulesRaw);
const moduleCodes = Object.keys(moduleByType);

const hammingDistance = (a, b) => {
    if (!a || !b || a.length !== b.length) return Number.MAX_SAFE_INTEGER;
    let d = 0;
    for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) d += 1;
    return d;
};

const nearestModuleCode = (target) => {
    if (moduleByType[target]) return target;
    let best = moduleCodes[0];
    let bestDist = Number.MAX_SAFE_INTEGER;
    moduleCodes.forEach((code) => {
        const dist = hammingDistance(target, code);
        if (dist < bestDist) {
            bestDist = dist;
            best = code;
        }
    });
    return best;
};

const mergeProfile = (typeCode) => {
    const base = baseByType[typeCode] || {};
    const direct = moduleByType[typeCode];
    const nearest = moduleByType[nearestModuleCode(typeCode)] || {};
    const source = direct || nearest;

    const merged = {
        ...base,
        ...source,
        type_code: typeCode,
        name: SEB_NAMES[typeCode] || source.name || base.name || `${typeCode} 유형`,
        definition: source.definition || source.summary || base.description || '',
        social_view: source.social_view || source.social_role || base.social_role || '',
        misunderstanding: source.misunderstanding || source.blind_spots || base.blind_spots || '',
        caution: source.caution || source.action_plan || base.action_plan || '',
        comfort_zone: source.comfort_zone || source.academic_report?.psychological_basis || base.academic_report?.psychological_basis || '',
        core_value: source.core_value || source.academic_report?.core_driver || base.academic_report?.core_driver || '',
        summary: source.summary || base.summary || '',
        description: source.description || base.description || source.summary || base.summary || '',
        detailed_analysis: source.detailed_analysis || base.detailed_analysis || '',
        social_role: source.social_role || base.social_role || '',
        blind_spots: source.blind_spots || base.blind_spots || '',
        action_plan: source.action_plan || base.action_plan || '',
        academic_report: {
            ...(base.academic_report || {}),
            ...(source.academic_report || {})
        }
    };

    return merged;
};

export const MEGA_TYPE_PROFILE_MAP = Object.fromEntries(
    Object.keys(baseByType).map((code) => [code, mergeProfile(code)])
);

export const getMegaTypeProfile = (typeCode) =>
    MEGA_TYPE_PROFILE_MAP[typeCode] || mergeProfile(typeCode);
